import { useEffect } from "react";
import {
  type IFrameObjectMessage,
  type IFrameRequestPayloadMap,
  IFrameResponseMap,
  type PreAppointmentRequestPayload,
  RequestEvent } from
"@pexip-engage-public/plugin-events";
import * as Sentry from "@sentry/remix";
import { v4 as uuidv4 } from "uuid";

import { sendMessage } from "~/hooks/useSendMessage";

type IFrameObjectMessageCb = (message: IFrameObjectMessage) => unknown;

class IFrameMessageHandler {
  private listeners = new Map<string, IFrameObjectMessageCb>();
  private requestData = <T extends IFrameObjectMessage["type"],>({
    payload,
    timeout,
    type




  }: {type: T;payload: IFrameRequestPayloadMap[T];timeout?: number;}) => {
    const id = uuidv4();

    return new Promise<IFrameResponseMap[T] | null>((resolve, reject) => {
      if ("parentIFrame" in window && window.parentIFrame) {
        sendMessage({ type, payload });
        timeout && window.setTimeout(reject, timeout);

        this.listeners.set(id, (event) => {
          if (event.type === type) {
            resolve((event.payload as any));
          }
        });
      } else {
        resolve(null);
      }
    }).finally(() => this.listeners.delete(id));
  };

  handleMessage = (event: IFrameObjectMessage) => this.listeners.forEach((cb) => cb(event));

  addListener = (listener: IFrameObjectMessageCb) => {
    const id = uuidv4();

    this.listeners.set(id, listener);

    return () => this.listeners.delete(id);
  };

  preAppointmentRequest = (payload: PreAppointmentRequestPayload) =>
  this.requestData({ type: RequestEvent.PRE_APPOINTMENT_REQUEST, payload, timeout: 1000 });

  getOrigin = () =>
  this.requestData({ type: RequestEvent.REQUEST_ORIGIN_URL, payload: null, timeout: 1000 });

  getCurrentPosition = async (): Promise<GeolocationPosition> => {
    const result = await this.requestData({
      type: RequestEvent.REQUEST_GEOLOCATION,
      payload: null
    });

    if (result === null) {
      return new Promise<GeolocationPosition>((res, rej) => {
        if (navigator && "geolocation" in navigator) {
          return navigator.geolocation.getCurrentPosition(res, rej);
        }

        rej("User location not Available");
      });
    }

    if ("error" in result) {
      throw new Error(result.error);
    }

    return result;
  };
}

export const iframeMessageHandler = new IFrameMessageHandler();

export function IFrameSetup() {
  useEffect(() => {
    window.iFrameResizer = {
      onMessage(message) {
        iframeMessageHandler.handleMessage(message);
      },
      onReady() {
        iframeMessageHandler.getOrigin().then((source) => {
          if (!source) return;

          if (source.href.length <= 200) {
            // ideally store as tag, fallback to context
            Sentry.setTag("pexip.source.href", source.href);
          } else {
            Sentry.setContext("pexip", { source });
          }
        });
      }
    };

    const cleanupFn = iframeMessageHandler.addListener((message) => {
      if (message.type === RequestEvent.CSS_VAR_UPDATE) {
        const root = document.querySelector(":root");

        if (root instanceof HTMLElement) {
          root.style.setProperty(message.payload.name, message.payload.value);
        }
      }

      if (message.type === RequestEvent.CUSTOM_CSS_UPDATE) {
        const styleSheet = getStyleSheet();
        styleSheet.replace(message.payload.css);
      }
    });

    import("iframe-resizer/js/iframeResizer.contentWindow.min.js");

    return () => {
      cleanupFn();
    };
  }, []);

  return null;
}

let sheet: CSSStyleSheet | null = null;
function getStyleSheet() {
  if (sheet) return sheet;

  sheet = new CSSStyleSheet();
  document.adoptedStyleSheets = [sheet];

  return sheet;
}